import { fetchOnboardingState } from "./onboarding";
import { signedInUserId, watchSignedInUser } from "./session-gate";

let exit: (() => void) | undefined;
let checking: { userId: string; promise: Promise<void> } | undefined;

/**
 * Who takes the person out of the firm once it is known they are out of it. The router lives in
 * `main.tsx`, so it registers here rather than being imported from the places that notice.
 */
export function registerFirmExit(handler: () => void): () => void {
  exit = handler;
  return () => {
    if (exit === handler) exit = undefined;
  };
}

/**
 * A refusal that might mean this person is no longer in the firm: a token the identity service
 * would not mint, or a Members read it no longer answers. Neither says so for certain (a token can
 * be refused for another reason), so this asks the question that does: onboarding, which the
 * service answers from the person's membership. Anything but `done` means no firm, and they are
 * taken out; a read that fails is not an answer, and changes nothing. Asked once at a time per
 * person, however many requests were refused together.
 *
 * The answer is about the person who was signed in when it was asked. If someone else has signed in
 * since (or no one), the question is dropped and the answer, should it arrive, is ignored: the exit
 * clears the browser's tokens, reads and chats, which are now someone else's to keep.
 */
export function checkFirmMembership(): Promise<void> {
  const asking = signedInUserId();
  // No one to ask about: a refusal that outlived its sign-in says nothing about whoever is next.
  if (asking === null) return Promise.resolve();
  if (checking?.userId === asking) return checking.promise;

  const controller = new AbortController();
  const stopWatching = watchSignedInUser(() => controller.abort());
  const promise: Promise<void> = (async () => {
    try {
      const onboarding = await fetchOnboardingState(controller.signal);
      if (
        onboarding.state !== "done" &&
        !controller.signal.aborted &&
        signedInUserId() === asking
      ) {
        exit?.();
      }
    } catch {
      // Not knowing is not the same as being out. The refusal that led here is still shown.
    }
  })().finally(() => {
    stopWatching();
    if (checking?.promise === promise) checking = undefined;
  });
  checking = { userId: asking, promise };
  return promise;
}
